import { isNumber } from 'radash';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { ConnectionHelpers } from '@diagram/helpers/ConnectionHelpers';
import { PortHelpers } from '@diagram/helpers/PortHelpers';
import type { RelativePowerFlowDirection } from '@diagram/services/PowerFlowService';

import {
    type DiagramComponentInstance,
    type DiagramConnection,
    isProtectionDevice,
    type PowerFlowDirection,
} from 'models';

const PowerFlowHelpers = {
    /**
     * How power flows over a connection, seen from its `from` end: `generate` when it sends power to `to`, `load`
     * when it draws power from `to`, and `bidirectional` when both ends carry power both ways. Null when an end has
     * no port, or the ends don't agree, like both taking power in.
     */
    getRelativePowerFlowDirection: (
        connection: DiagramConnection,
        fromComponentInstance: DiagramComponentInstance,
        toComponentInstance: DiagramComponentInstance,
    ): RelativePowerFlowDirection | null => {
        ConnectionHelpers.__validateHelperArguments(connection, fromComponentInstance, toComponentInstance);

        const { from, to } = connection;

        if (!isNumber(from.port) || !isNumber(to.port)) {
            return null;
        }

        const fromPowerFlowDirection = fromComponentInstance.configuration.ports[from.port]?.powerFlowDirection;
        const toPowerFlowDirection = toComponentInstance.configuration.ports[to.port]?.powerFlowDirection;

        if (fromPowerFlowDirection === 'bidirectional' && toPowerFlowDirection === 'bidirectional') {
            return 'bidirectional';
        }

        if (
            (fromPowerFlowDirection === 'bidirectional' && toPowerFlowDirection === 'input') ||
            (fromPowerFlowDirection === 'output' && toPowerFlowDirection === 'input') ||
            (toPowerFlowDirection === 'bidirectional' && fromPowerFlowDirection === 'output') ||
            (toPowerFlowDirection === 'input' && fromPowerFlowDirection === 'output')
        ) {
            return 'generate';
        }

        if (
            (fromPowerFlowDirection === 'bidirectional' && toPowerFlowDirection === 'output') ||
            (fromPowerFlowDirection === 'input' && toPowerFlowDirection === 'output') ||
            (toPowerFlowDirection === 'bidirectional' && fromPowerFlowDirection === 'input') ||
            (toPowerFlowDirection === 'output' && fromPowerFlowDirection === 'input')
        ) {
            return 'load';
        }

        return null;
    },

    /**
     * The directions a port can be wired with. A component whose specification defines its direction (see
     * definesPowerFlowDirection) is limited to that direction, anything else to what its type allows.
     */
    getAllowedPowerFlowDirections: (
        componentInstance: DiagramComponentInstance,
        index: DiagramConnection['from' | 'to']['port'],
    ) => {
        if (index === null) {
            return [];
        }

        const declared = PortHelpers.getSpecifications(componentInstance, index)?.powerFlowDirection;

        // Anything that declares no direction, including products with incomplete catalogue data, can do whatever its
        // type allows.
        const capabilities: PowerFlowDirection[] =
            declared && PowerFlowHelpers.definesPowerFlowDirection(componentInstance)
                ? [declared]
                : ComponentInstanceHelpers.definition(componentInstance.componentType).ports.powerFlowDirections;

        // A port that can carry power both ways can also be wired to carry it one way.
        return (['bidirectional', 'input', 'output'] as PowerFlowDirection[]).filter(
            (powerFlowDirection) =>
                capabilities.includes(powerFlowDirection) ||
                (powerFlowDirection !== 'bidirectional' && capabilities.includes('bidirectional')),
        );
    },

    /**
     * The port that power entering `port` has to leave by, or null when nothing forces it out one particular port.
     * Two-port devices and converters without energy storage always pass power through. For anything else with two
     * ports or two connected ports that is only a guess, made when `guessPassThrough` is set.
     */
    getPassThroughPort: (
        componentInstance: DiagramComponentInstance,
        port: number,
        connectedPorts: number[],
        guessPassThrough: boolean,
    ): number | null => {
        const { ports } = ComponentInstanceHelpers.definition(componentInstance.componentType);
        const actsAsConverter = ComponentInstanceHelpers.actsAsConverter(componentInstance);

        // Consumers and sources (loads, HVAC, solar, ...) take or give power on every port, so nothing flows through them.
        const passesPowerThrough =
            ports.powerFlowDirections.includes('bidirectional') ||
            (ports.powerFlowDirections.includes('input') && ports.powerFlowDirections.includes('output'));

        if (!passesPowerThrough) {
            return null;
        }

        const hasTwoPorts = componentInstance.configuration.ports.length === 2;

        // A converter that stores energy, like a solution with a battery inside, can take power in on both sides.
        const hasEnergyStorage =
            'energyCapacity' in componentInstance.specifications.electrical &&
            isNumber(componentInstance.specifications.electrical.energyCapacity?.value);

        // Power entering a two-port device or converter can only leave by the other port. Anything else with two ports
        // or two connections, like a bus feeding two loads, only might pass it on.
        const certain = hasTwoPorts && (ports.max === 2 || (!!actsAsConverter && !hasEnergyStorage));

        if (!certain && !guessPassThrough) {
            return null;
        }

        if (hasTwoPorts) {
            return port === 0 ? 1 : 0;
        }

        if (connectedPorts.length === 2 && connectedPorts.includes(port)) {
            return connectedPorts.find((connectedPort) => connectedPort !== port) ?? null;
        }

        return null;
    },

    /**
     * Whether the specification's direction limits how the component can be wired. It does for linked products and
     * protection devices, whose specified direction is what the device can do. Other components only have the
     * direction they are wired with.
     */
    definesPowerFlowDirection: (componentInstance: DiagramComponentInstance) =>
        !!componentInstance.componentId || isProtectionDevice(componentInstance.componentType),

    /**
     * The direction on the other end of a wire, or on the other port of a pass-through component: input and output
     * swap, bidirectional stays.
     */
    getReversedPowerFlowDirection: (powerFlowDirection: PowerFlowDirection) => {
        const reversed: {
            [key: string]: PowerFlowDirection;
        } = {
            input: 'output',
            bidirectional: 'bidirectional',
            output: 'input',
        };

        return reversed[powerFlowDirection];
    },
};

export { PowerFlowHelpers };
